import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="hierarchical-checkbox"
export default class extends Controller {
  static targets = ['checkbox', 'groupContent', 'groupIcon', 'groupCount', 'groupToggle'];

  connect() {
    this.buildRelationshipMaps();
    // Counts and toggle states are derived, not server-rendered, so seed them
    // on load — otherwise a group header stays blank until its first click.
    this.updateGroupCounts();
    this.syncGroupToggles();
  }

  buildRelationshipMaps() {
    // Reset maps
    this.checkboxById = new Map();
    this.childrenByParent = new Map();
    this.parentByChild = new Map();
    this.allDescendantsCache = new Map();

    // Build relationship maps for O(1) lookups
    this.checkboxTargets.forEach(checkbox => {
      const itemId = checkbox.dataset.itemId;
      const parentId = checkbox.dataset.parentId;

      // Store checkbox by ID for direct lookup
      this.checkboxById.set(itemId, checkbox);

      // Build parent-child relationships
      if (parentId) {
        // This checkbox has a parent
        if (!this.childrenByParent.has(parentId)) {
          this.childrenByParent.set(parentId, []);
        }
        this.childrenByParent.get(parentId).push(checkbox);
        this.parentByChild.set(itemId, parentId);
      }
    });
  }

  checkboxChange(event) {
    const checkbox = event.target;
    const checked = checkbox.checked;
    const checkboxId = checkbox.dataset.itemId;

    if (checked) {
      // When checking a child, also check its parent (but NOT the other way around)
      this.selectAncestors(checkboxId);
    } else {
      // When unchecking a parent, uncheck all descendants
      this.updateDescendants(checkboxId, false);
      // When unchecking a child, uncheck parent only if no other children are checked
      this.updateAncestorsOnUncheck(checkboxId);
    }

    this.updateGroupCounts();
    this.syncGroupToggles();
  }

  // A header checkbox that selects or clears every descendant at once. Kept
  // separate from checkboxChange, where checking a parent must NOT cascade down
  // — an MCC manager account is selectable on its own, without its whole
  // sub-account tree. A groupToggle is a header control only; it carries no
  // form value of its own.
  toggleGroupSelection(event) {
    const groupId = event.target.dataset.groupId;
    if (!groupId) return;

    this.updateDescendants(groupId, event.target.checked);
    this.updateGroupCounts();
    this.syncGroupToggles();
  }

  // Mirrors each group header checkbox onto its descendants: checked when all
  // are selected, indeterminate on a partial selection. No-ops for lists that
  // declare no groupToggle targets.
  syncGroupToggles() {
    this.groupToggleTargets.forEach(toggle => {
      const leaves = this.getLeafDescendants(toggle.dataset.groupId);
      const selected = leaves.filter(leaf => leaf.checked).length;

      toggle.checked = leaves.length > 0 && selected === leaves.length;
      toggle.indeterminate = selected > 0 && selected < leaves.length;
    });
  }

  updateDescendants(itemId, checked) {
    // Get all direct children of this item
    const children = this.childrenByParent.get(itemId) || [];

    children.forEach(childCheckbox => {
      // Skip disabled checkboxes
      if (childCheckbox.disabled) {
        return;
      }

      // Set the child's checked state
      childCheckbox.checked = checked;

      // Recursively update the child's descendants
      const childId = childCheckbox.dataset.itemId;
      this.updateDescendants(childId, checked);
    });
  }

  selectAncestors(itemId) {
    // When a child is checked, also check its parent (and grandparent, etc.)
    const parentId = this.parentByChild.get(itemId);

    if (!parentId) {
      return; // No parent, we're done
    }

    const parentCheckbox = this.checkboxById.get(parentId);
    if (!parentCheckbox) {
      return; // Parent checkbox not found
    }

    // Don't check disabled checkboxes
    if (parentCheckbox.disabled) {
      return;
    }

    // Check the parent
    parentCheckbox.checked = true;

    // Recursively check ancestors
    this.selectAncestors(parentId);
  }

  updateAncestorsOnUncheck(itemId) {
    const parentId = this.parentByChild.get(itemId);

    if (!parentId) {
      return; // No parent, we're done
    }

    const parentCheckbox = this.checkboxById.get(parentId);
    if (!parentCheckbox) {
      return; // Parent checkbox not found
    }

    // Don't modify disabled checkboxes
    if (parentCheckbox.disabled) {
      return;
    }

    // Get all children of the parent
    const siblings = this.childrenByParent.get(parentId) || [];

    // Only uncheck parent if NO children are checked
    const anySiblingChecked = siblings.some(sibling => sibling.checked);

    if (!anySiblingChecked) {
      parentCheckbox.checked = false;
      // Recursively update the parent's ancestors
      this.updateAncestorsOnUncheck(parentId);
    }
  }

  // Collapsible group toggle
  toggleGroup(event) {
    const row = event.currentTarget;
    // data-group-id may be on the element itself or on a child
    const groupId = row.dataset.groupId || row.querySelector('[data-group-id]')?.dataset?.groupId;
    if (!groupId) return;

    // Find matching content and icon targets
    const content = this.groupContentTargets.find(el => el.dataset.groupId === groupId);
    const icon = this.groupIconTargets.find(el => el.dataset.groupId === groupId);

    if (content) {
      content.classList.toggle('hidden');
    }

    if (icon) {
      if (content && !content.classList.contains('hidden')) {
        icon.style.transform = 'rotate(90deg)';
      } else {
        icon.style.transform = 'rotate(0deg)';
      }
    }
  }

  // Prevent click events on checkboxes from toggling the group
  stopPropagation(event) {
    event.stopPropagation();
  }

  // Update selected counts on group headers
  updateGroupCounts() {
    this.groupCountTargets.forEach(countEl => {
      const groupId = countEl.dataset.groupId;
      const leafCheckboxes = this.getLeafDescendants(groupId);
      const total = leafCheckboxes.length;
      const selected = leafCheckboxes.filter(cb => cb.checked).length;
      countEl.textContent = `${selected}/${total} selected`;
    });
  }

  getLeafDescendants(itemId) {
    const children = this.childrenByParent.get(itemId) || [];
    if (children.length === 0) {
      const checkbox = this.checkboxById.get(itemId);
      return checkbox ? [checkbox] : [];
    }

    return children.flatMap(child => {
      const childId = child.dataset.itemId;
      const grandchildren = this.childrenByParent.get(childId) || [];
      if (grandchildren.length === 0) {
        return [child];
      }
      return this.getLeafDescendants(childId);
    });
  }

  // Select All / Deselect All methods
  selectAll(event) {
    const managerId = event.target.dataset.managerId;
    const managerCheckbox = this.checkboxById.get(managerId);

    if (managerCheckbox) {
      managerCheckbox.checked = true;
      this.updateDescendants(managerId, true);
    }

    this.updateGroupCounts();
    this.syncGroupToggles();
  }

  deselectAll(event) {
    const managerId = event.target.dataset.managerId;

    // Deselect the manager checkbox and all descendants
    const managerCheckbox = this.checkboxById.get(managerId);
    if (managerCheckbox) {
      managerCheckbox.checked = false;
    }
    this.updateDescendants(managerId, false);

    // Update ancestor state
    this.updateAncestorsOnUncheck(managerId);
    this.updateGroupCounts();
    this.syncGroupToggles();
  }
}
